import { useEffect, useState, useMemo } from "react";
import { Button, Col, Row, Form } from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import TextInput from "../../../components/textInput/TextInput";
import SearchBar from "../../../components/searchBar/SearchBar";
import { getCustomerIdentity } from "../../../store/actions/fault.action";
import { useLocation } from "react-router-dom";
import MainLoader from "../../../components/loading/MainLoader";
import AlertModal from "../../../components/alertModal/alertModal";
import { useDispatch } from "react-redux";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import { BASE_URL } from "../../../const/URL";
import ServiceModificationSuccessMessage from "./ServiceModificationSuccess/ServiceModificationSuccess";

interface IModifiableAttribute {
  attributeName: string;
  attributeValue: string | null;
  attributeType: "search" | "dropdown" | "text";
  attributeLov: string | null;
}

interface IServiceModificationResponse {
  dataBundle: {
    productName: string;
    address: string;
    modifiayableAttributes: IModifiableAttribute[];
  };
  isSuccess: boolean;
  errorMessage: string;
  successMessage: string;
}

interface IServiceModificationRequest {
  description: string;
  sltAltContactNumber: string;
  serviceId: string;
  customerReferenceNumber: string;
  serviceAddress: string;
  serviceLocationIdentifier?: string;
}

interface IErrorResponse {
  result: boolean;
  message: string;
  displayMessage: string;
  statusCode: number;
  isSuccess: boolean;
}

interface IFormInputs {
  serviceId: string;
  serviceLocationIdentifier: string;
  modifications: Record<string, string>;
}

const SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH = 20;
const SERVICE_LOCATION_IDENTIFIER_PLACEHOLDER =
  "You may provide a location identifier for ease of reference (limit this to maximum 20 characters)";

const ServiceModification = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { isDirty },
  } = useForm<IFormInputs>();

  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [serviceDisplay, setServiceDisplay] = useState("");
  const [serviceAddress, setServiceAddress] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState("");
  const [ServiceID, setServiceIDValue] = useState("");
  const [success, setSuccess] = useState("");
  const [referenceID, setReferenceID] = useState("");
  const [contactPhone] = useState("0710000000");
  const [modifiableAttributes, setModifiableAttributes] = useState<
    IModifiableAttribute[]
  >([]);
  const [initialValues, setInitialValues] = useState<Record<string, string>>(
    {}
  );
  const [hasChanges, setHasChanges] = useState(false);
  const dispatch: any = useDispatch();

  const currentModifications = useWatch({
    control,
    name: "modifications",
    defaultValue: {},
  });

  // const hasChanges = useMemo(() => {
  //   return Object.entries(currentModifications).some(
  //     ([key, value]) => initialValues[key] !== value
  //   );
  // }, [currentModifications, initialValues]);

  const serviceParamID = (state as any)?.customerAccountNumber;

  const formValues = useWatch({
    control,
    defaultValue: { modifications: {} },
  });

  useEffect(() => {
    if (Object.keys(initialValues).length === 0) return;

    const changesExist = Object.entries(formValues.modifications || {}).some(
      ([key, value]) => initialValues[key] !== value
    );
    setHasChanges(changesExist);
  }, [formValues, initialValues]);

  useEffect(() => {
    if (serviceParamID) {
      handleSearch(serviceParamID);
    }
  }, [serviceParamID]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("enterprise_portal_access_Token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const fetchServiceModificationData = async (serviceId: string) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/service-modification/by-service-id/${serviceId}`,
        {
          method: "GET",
          headers: getAuthHeader(),
        }
      );
      const data: IServiceModificationResponse = await response.json();

      if (!response.ok) {
        throw new Error(data?.errorMessage || "API request failed");
      }

      if (!data.isSuccess) {
        throw new Error(data.errorMessage || "API request failed");
      }

      return data;
    } catch (error: any) {
      console.error("Error fetching service modification data:", error);
      throw new Error(
        error.message || "Failed to fetch service modification data"
      );
    }
  };

  const handleSearch = (value: string) => {
    if (!value) return;

    setIsVerified(false);
    setServiceIDValue("");
    setServiceAddress("");
    setServiceDisplay("");
    setError("");
    setSuccess("");
    setModifiableAttributes([]);
    setInitialValues({});

    reset({
      serviceId: "",
      serviceLocationIdentifier: "",
      modifications: {},
    });

    setIsLoading(true);
    getCustomerIdentity(value)
      .then((response) => {
        setIsVerified(true);
        setServiceIDValue(value);

        fetchServiceModificationData(value)
          .then((modificationData) => {
            setServiceDisplay(modificationData.dataBundle.productName);
            setServiceAddress(modificationData.dataBundle.address);
            setModifiableAttributes(
              modificationData.dataBundle.modifiayableAttributes
            );

            const initialModifications: Record<string, string> = {};
            modificationData.dataBundle.modifiayableAttributes.forEach(
              (attr) => {
                if (attr.attributeValue !== null) {
                  initialModifications[attr.attributeName] =
                    attr.attributeValue;
                }
              }
            );

            setInitialValues(initialModifications);
            setValue("modifications", initialModifications);
            setHasChanges(false);

            setIsLoading(false);
          })
          .catch((error) => {
            setIsLoading(false);
            setShowAlert(true);
            setError(
              error.message || "Failed to load service modification options"
            );
          });
      })
      .catch((error: IErrorResponse) => {
        setIsLoading(false);
        setIsVerified(false);
        setShowAlert(true);
        setError(error?.message || "An unexpected error occurred");
      });
  };

  useEffect(() => {
    if (ServiceID) {
      setValue("serviceId", ServiceID);
    }
  }, [ServiceID, setValue]);

  const handleFormSubmit = (data: IFormInputs) => {
    const changedModifications = Object.entries(data.modifications)
      .filter(([key, value]) => initialValues[key] !== value)
      .map(([attrName, attrValue]) => `${attrName}-${attrValue}`)
      .join(",");

    const requestData: IServiceModificationRequest = {
      description: changedModifications,
      sltAltContactNumber: contactPhone,
      serviceId: data.serviceId,
      customerReferenceNumber: getCustomerReferenceNumber(),
      serviceAddress,
      serviceLocationIdentifier: data.serviceLocationIdentifier?.trim() || "",
    };

    setIsLoading(true);
    fetch(
      `${BASE_URL}/api/v1/EnterprisePortal.ServiceAssuaranceAPI/api/ServiceAssuarance/CreateServiceModificationRequest`,
      {
        method: "POST",
        headers: getAuthHeader(),
        body: JSON.stringify(requestData),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.isSuccess) {
          setSuccess(data?.successMessage || "Request submitted successfully");
          setShowAlert(true);
          reset({
            serviceId: "",
            serviceLocationIdentifier: "",
            modifications: {},
          });
          setServiceIDValue("");
          setServiceDisplay("");
          setServiceAddress("");
          setReferenceID(data?.result);
          setIsVerified(false);
          setModifiableAttributes([]);
          setInitialValues({});
        } else {
          throw new Error(data.errorMessage || "Failed to submit request");
        }
      })
      .catch((error) => {
        setShowAlert(true);
        setError(
          error.message || "Failed to submit service modification request"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderAttributeInput = (attribute: IModifiableAttribute) => {
    switch (attribute.attributeType) {
      case "search":
      case "dropdown":
        const options = attribute.attributeLov?.split(",") || [];
        return (
          <Form.Group controlId={attribute.attributeName}>
            <Form.Label>{attribute.attributeName}</Form.Label>
            <Form.Select
              {...register(`modifications.${attribute.attributeName}`)}
              defaultValue={attribute.attributeValue || ""}
            >
              <option value="">Select {attribute.attributeName}</option>
              {options.map((option) => (
                <option key={option} value={option.trim()}>
                  {option.trim()}
                </option>
              ))}
            </Form.Select>
            {attribute.attributeValue &&
              attribute.attributeType !== "dropdown" &&
              attribute.attributeType !== "search" && (
                <Form.Text className="text-muted">
                  {attribute.attributeValue}
                </Form.Text>
              )}
          </Form.Group>
        );
      case "text":
        return (
          <Form.Group controlId={attribute.attributeName}>
            <Form.Label>{attribute.attributeName}</Form.Label>
            <Form.Control
              type="text"
              {...register(`modifications.${attribute.attributeName}`)}
              defaultValue={attribute.attributeValue || ""}
              placeholder={`Enter ${attribute.attributeName}`}
            />
            {attribute.attributeValue && (
              <Form.Text className="text-muted">
                {attribute.attributeValue}
              </Form.Text>
            )}
          </Form.Group>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/serviceRequest/serviceModification",
            title: "Service Modification",
          },
        ]}
      />

      {error && (
        <AlertModal
          show={showAlert}
          variant="error"
          buttons={[
            {
              text: "Ok",
              onClick: () => setShowAlert(false),
              variant: "success",
            },
          ]}
        >
          <label>{error}</label>
        </AlertModal>
      )}

      <MainLoader show={isLoading} />

      {/* {success && (
        <AlertModal
          show={showAlert}
          variant="success"
          buttons={[
            {
              text: "Ok",
              onClick: () => setShowAlert(false),
              variant: "success",
            },
          ]}
        >
          <label>{success}</label>
        </AlertModal>
      )} */}

      {success ? (
        <ServiceModificationSuccessMessage
          handleClose={() => setSuccess("")}
          referenceNo={referenceID || ""}
          faultType={"service"}
        />
      ) : (
        <>
          <Row className="justify-content-center mt-2 mb-5">
            <Col className="col-md-5">
              <SearchBar
                onSearch={handleSearch}
                defaultValue={serviceParamID}
                placeholder="Service Number / ID"
              />
            </Col>
          </Row>

          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="poppins-semibold"
            style={{
              opacity: isVerified ? 1 : 0.6,
              pointerEvents: isVerified ? "auto" : "none",
              filter: isVerified ? "none" : "blur(2px)",
              backgroundColor: isVerified ? "white" : "#f0f0f0",
              transition: "all 0.3s ease-in-out",
              padding: "15px",
              borderRadius: "8px",
            }}
          >
            <h5 className="mb-4">Existing Information</h5>
            <Row className="row-cols-1 row-cols-md-2 gy-3 mb-4">
              <Col>
                <TextInput
                  disabled
                  label="Service Name"
                  color="gray"
                  value={serviceDisplay}
                />
              </Col>
              <Col>
                <TextInput
                  disabled
                  label="Service Address"
                  color="gray"
                  value={serviceAddress}
                />
              </Col>
            </Row>

            {isVerified && (
              <>
                <h5 className="mb-4">Modification Options</h5>
                <Row className="row-cols-1 gy-3 mb-4">
                  <Col className="mb-3">
                    <Form.Group controlId="serviceLocationIdentifier">
                      <Form.Label>Service Location Identifier</Form.Label>
                      <Form.Control
                        type="text"
                        maxLength={SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH}
                        placeholder={SERVICE_LOCATION_IDENTIFIER_PLACEHOLDER}
                        {...register("serviceLocationIdentifier")}
                      />
                    </Form.Group>
                  </Col>
                  {modifiableAttributes.map((attribute) => (
                    <Col key={attribute.attributeName} className="mb-3">
                      {renderAttributeInput(attribute)}
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <Row className="mt-5 justify-content-center">
              <Col className="col-md-3">
                <Button
                  type="submit"
                  className="w-100"
                  // style={{
                  //   backgroundColor: hasChanges ? "#51B848" : "#cccccc",
                  //   borderColor: hasChanges ? "#51B848" : "#cccccc",
                  //   fontSize: "16px",
                  // }}
                  style={{
                    backgroundColor: "#51B848",
                    borderColor: "#51B848",
                    fontSize: "16px",
                  }}
                  // disabled={!isVerified || !hasChanges}
                  disabled={!isVerified}
                >
                  {/* {hasChanges ? "Submit Changes" : "No Changes Made"} */}
                  Submit
                </Button>
              </Col>
            </Row>
          </form>
        </>
      )}
    </div>
  );
};

export default ServiceModification;
